import React, { useEffect, useMemo, useState } from 'react'
import { security, USER_ROLES } from '../config/security'
import './AppSidebar.css'

function SidebarLink({ icon, label, active, onClick, filtro }) {
  if (filtro && !label.toLowerCase().includes(filtro.toLowerCase())) return null
  return (
    <li className="app-sidebar-item">
      <button
        type="button"
        className={`app-sidebar-link ${active ? 'active' : ''}`}
        onClick={onClick}
        data-bs-dismiss="offcanvas"
      >
        <span className="app-sidebar-icon" aria-hidden>{icon}</span>
        <span className="app-sidebar-text">{label}</span>
      </button>
    </li>
  )
}

function SidebarGroup({ title, icon, children, filtro, abertoPorAtivo }) {
  const kids = React.Children.toArray(children).filter((child) => {
    if (!React.isValidElement(child)) return false
    const label = child.props.label || ''
    if (filtro && !label.toLowerCase().includes(String(filtro).toLowerCase())) return false
    return true
  })
  const [aberto, setAberto] = useState(abertoPorAtivo)
  useEffect(() => {
    if (abertoPorAtivo || filtro) setAberto(true)
  }, [abertoPorAtivo, filtro])
  if (kids.length === 0) return null
  return (
    <li className={`app-sidebar-item ${aberto ? 'is-open' : ''}`}>
      <button
        type="button"
        className="app-sidebar-link app-sidebar-toggle"
        onClick={() => setAberto((v) => !v)}
        aria-expanded={aberto}
      >
        <span className="app-sidebar-icon" aria-hidden>{icon}</span>
        <span className="app-sidebar-text">{title}</span>
        <span className={`app-sidebar-arrow ${aberto ? 'is-open' : ''}`} aria-hidden>›</span>
      </button>
      {aberto && <ul className="app-sidebar-tree">{kids}</ul>}
    </li>
  )
}

export default function AppSidebar({
  user,
  userRole,
  chatZap,
  whatsappProposta,
  currentView,
  permissoesMenusCache,
  hasPermissoesCustomizadas,
  onNavigate,
  onOpenValorKit,
  onOpenKitsPersonalizados,
  onOpenConsultaPropostas,
  onOpenListaMaterial,
  onOpenEmpresas,
  onOpenChangePassword,
  onLogout
}) {
  const [filtro, setFiltro] = useState('')
  const can = (menu) => security.canAccessMenu(userRole, menu, permissoesMenusCache, hasPermissoesCustomizadas)
  const isAdmin = userRole === USER_ROLES.ADMINISTRADOR
  const isInstalador = userRole === 'instalador'
  const chatZapLiberado = Number(chatZap) === 1
  const propostaLiberada = Number(whatsappProposta) === 1
  const busca = filtro.trim()

  const ativoEm = (...views) => views.includes(currentView)

  const nomeUsuario = user?.nome || user?.email || 'Usuário'
  const inicial = String(nomeUsuario).trim().charAt(0).toUpperCase() || 'U'

  const grupoComercialAberto = ativoEm(
    'clientes_ii', 'cadastrar_clientes', 'vendas', 'leads', 'gestao_leads',
    'cadastrar_leads', 'leads_sdr', 'leads_capacitacao', 'propostas_cadastro', 'orcamentos'
  )
  const grupoOperacaoAberto = ativoEm(
    'clientes', 'ordem_servicos', 'servicos', 'gestao_instalacao', 'vistorias', 'presenca', 'painel_processos'
  )
  const grupoEngenhariaAberto = ativoEm('engenharia_homologacao', 'fluxo_cliente', 'engenharia_inversores')
  const grupoCadastrosAberto = ativoEm(
    'colaboradores', 'equipes', 'usuarios', 'cargos', 'veiculos',
    'permissoes_menus', 'tipos_servico', 'tipos_padrao', 'cadastro_inversores_modulos'
  )
  const grupoFinanceiroAberto = ativoEm(
    'financeiro', 'lancar_movimentacoes', 'cadastro_tipos_movimentacao',
    'pagamento_colaborador', 'adiantamento', 'medicao'
  )
  const grupoFerramentasAberto = ativoEm(
    'calculadora_solar', 'calendario', 'whatsapp', 'simulacao_fotus', 'kit_fornecedores'
  )

  const roleLabel = useMemo(() => {
    const mapa = {
      administrador: 'Administrador',
      administrativo: 'Administrativo',
      instalador: 'Instalador',
      especialista: 'Especialista',
      gerente: 'Gerente',
      gerente_comercial: 'Gerente Comercial',
      homologador: 'Homologador',
      campo: 'Campo',
      vistoriador: 'Vistoriador',
      pap: 'PAP',
      sdr: 'SDR',
      avulso: 'Avulso'
    }
    return mapa[userRole] || userRole || 'Usuário'
  }, [userRole])

  return (
    <>
      <nav className="app-header navbar navbar-expand">
        <div className="container-fluid app-header-inner">
          {userRole && (
            <button
              className="app-header-toggle"
              type="button"
              data-bs-toggle="offcanvas"
              data-bs-target="#sidebarMenu"
              aria-controls="sidebarMenu"
              aria-label="Abrir menu"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" fill="currentColor" viewBox="0 0 16 16" aria-hidden>
                <path fillRule="evenodd" d="M2.5 12a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5m0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5m0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5" />
              </svg>
            </button>
          )}
          <div className="app-header-brand">
            <img src="/LogoConectSolar.png" alt="ConectSolar" />
          </div>
        </div>
      </nav>

      <div
        className="offcanvas offcanvas-start app-sidebar"
        tabIndex="-1"
        id="sidebarMenu"
        aria-label="Menu de navegação"
      >
        <div className="app-sidebar-brand">
          <img src="/LogoConectSolar.png" alt="ConectSolar" />
          <button type="button" className="app-sidebar-close" data-bs-dismiss="offcanvas" aria-label="Fechar menu">
            ×
          </button>
        </div>

        <div className="app-sidebar-user">
          <div className="app-sidebar-avatar" aria-hidden>{inicial}</div>
          <div className="app-sidebar-user-meta">
            <strong>{nomeUsuario}</strong>
            <small>{roleLabel}</small>
          </div>
        </div>

        <div className="app-sidebar-search">
          <input
            type="search"
            placeholder="Filtrar menu"
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
            aria-label="Filtrar menu"
          />
        </div>

        <div className="offcanvas-body app-sidebar-body">
          <ul className="app-sidebar-menu">
            <li className="app-sidebar-header">MENU</li>
            <SidebarLink icon="🏠" label="Dashboard" active={currentView === 'dashboard'} onClick={() => onNavigate('dashboard')} filtro={busca} />
            {can('dashboard_v2') && (
              <SidebarLink icon="📊" label="Dashboard V2" active={currentView === 'dashboard_v2'} onClick={() => onNavigate('dashboard_v2')} filtro={busca} />
            )}
            {can('monitoramento') && (
              <SidebarLink icon="🔆" label="Monitoramento" active={currentView === 'monitoramento'} onClick={() => onNavigate('monitoramento')} filtro={busca} />
            )}

            {isInstalador ? (
              <>
                <SidebarLink icon="🛠️" label="Gestão de Instalação" active={currentView === 'gestao_instalacao'} onClick={() => onNavigate('gestao_instalacao')} filtro={busca} />
                {can('painel_processos') && <SidebarLink icon="📌" label="Painel de Processos" active={currentView === 'painel_processos'} onClick={() => onNavigate('painel_processos')} filtro={busca} />}
              </>
            ) : (
              <>
                <SidebarGroup title="Comercial" icon="💼" filtro={busca} abertoPorAtivo={grupoComercialAberto}>
                  {can('clientes_ii') && <SidebarLink icon="💼" label="Gestão de Clientes" active={currentView === 'clientes_ii'} onClick={() => onNavigate('clientes_ii')} filtro={busca} />}
                  {can('cadastrar_clientes') && <SidebarLink icon="📝" label="Cadastrar Clientes" active={currentView === 'cadastrar_clientes'} onClick={() => onNavigate('cadastrar_clientes')} filtro={busca} />}
                  {can('vendas') && <SidebarLink icon="📈" label="Vendas" active={currentView === 'vendas'} onClick={() => onNavigate('vendas')} filtro={busca} />}
                  {can('leads') && <SidebarLink icon="🎯" label="Leads" active={currentView === 'leads'} onClick={() => onNavigate('leads')} filtro={busca} />}
                  {can('gestao_leads') && <SidebarLink icon="🗂️" label="Gestão Leads" active={currentView === 'gestao_leads'} onClick={() => onNavigate('gestao_leads')} filtro={busca} />}
                  {can('cadastrar_leads') && <SidebarLink icon="📝" label="Cadastrar Leads" active={currentView === 'cadastrar_leads'} onClick={() => onNavigate('cadastrar_leads')} filtro={busca} />}
                  {can('leads_sdr') && <SidebarLink icon="📞" label="Leads SDR" active={currentView === 'leads_sdr'} onClick={() => onNavigate('leads_sdr')} filtro={busca} />}
                  {can('leads_capacitacao') && <SidebarLink icon="📥" label="Leads Capacitação" active={currentView === 'leads_capacitacao'} onClick={() => onNavigate('leads_capacitacao')} filtro={busca} />}
                  {can('consulta_propostas') && <SidebarLink icon="📄" label="Consulta de Propostas" active={false} onClick={onOpenConsultaPropostas} filtro={busca} />}
                  {can('propostas_cadastro') && <SidebarLink icon="📊" label="Propostas (Cadastro)" active={currentView === 'propostas_cadastro'} onClick={() => onNavigate('propostas_cadastro')} filtro={busca} />}
                  {can('orcamentos') && <SidebarLink icon="📋" label="Orçamentos" active={currentView === 'orcamentos'} onClick={() => onNavigate('orcamentos')} filtro={busca} />}
                </SidebarGroup>

                <SidebarGroup title="Operação" icon="⚡" filtro={busca} abertoPorAtivo={grupoOperacaoAberto}>
                  {can('clientes') && <SidebarLink icon="🛠️" label="Instalação" active={currentView === 'clientes'} onClick={() => onNavigate('clientes')} filtro={busca} />}
                  {can('ordem_servicos') && <SidebarLink icon="🔧" label="Ordem de Serviços" active={currentView === 'ordem_servicos'} onClick={() => onNavigate('ordem_servicos')} filtro={busca} />}
                  {can('servicos') && <SidebarLink icon="📑" label="Serviços" active={currentView === 'servicos'} onClick={() => onNavigate('servicos')} filtro={busca} />}
                  {can('gestao_instalacao') && <SidebarLink icon="⚡" label="Gestão Instalação" active={currentView === 'gestao_instalacao'} onClick={() => onNavigate('gestao_instalacao')} filtro={busca} />}
                  {can('vistorias') && <SidebarLink icon="📋" label="Vistorias" active={currentView === 'vistorias'} onClick={() => onNavigate('vistorias')} filtro={busca} />}
                  {can('painel_processos') && <SidebarLink icon="📌" label="Painel de Processos" active={currentView === 'painel_processos'} onClick={() => onNavigate('painel_processos')} filtro={busca} />}
                  {can('presenca') && <SidebarLink icon="✅" label="Presenças" active={currentView === 'presenca'} onClick={() => onNavigate('presenca')} filtro={busca} />}
                </SidebarGroup>

                <SidebarGroup title="Engenharia" icon="🏗️" filtro={busca} abertoPorAtivo={grupoEngenhariaAberto}>
                  {can('engenharia_homologacao') && <SidebarLink icon="🏗️" label="Homologação" active={currentView === 'engenharia_homologacao'} onClick={() => onNavigate('engenharia_homologacao')} filtro={busca} />}
                  {can('fluxo_cliente') && <SidebarLink icon="🧭" label="Fluxo do Cliente" active={currentView === 'fluxo_cliente'} onClick={() => onNavigate('fluxo_cliente')} filtro={busca} />}
                  {can('engenharia_inversores') && <SidebarLink icon="🏗️" label="Engenharia" active={currentView === 'engenharia_inversores'} onClick={() => onNavigate('engenharia_inversores')} filtro={busca} />}
                </SidebarGroup>

                <SidebarGroup title="Cadastros" icon="👥" filtro={busca} abertoPorAtivo={grupoCadastrosAberto}>
                  {can('colaboradores') && <SidebarLink icon="👷" label="Colaboradores" active={currentView === 'colaboradores'} onClick={() => onNavigate('colaboradores')} filtro={busca} />}
                  {can('equipes') && <SidebarLink icon="👨‍👦‍👦" label="Equipes" active={currentView === 'equipes'} onClick={() => onNavigate('equipes')} filtro={busca} />}
                  {can('usuarios') && <SidebarLink icon="👤" label="Usuários" active={currentView === 'usuarios'} onClick={() => onNavigate('usuarios')} filtro={busca} />}
                  {can('cargos') && <SidebarLink icon="🎯" label="Cargos" active={currentView === 'cargos'} onClick={() => onNavigate('cargos')} filtro={busca} />}
                  {can('veiculos') && <SidebarLink icon="🚗" label="Veículos" active={currentView === 'veiculos'} onClick={() => onNavigate('veiculos')} filtro={busca} />}
                  {isAdmin && <SidebarLink icon="🎛️" label="Permissões de Menus" active={currentView === 'permissoes_menus'} onClick={() => onNavigate('permissoes_menus')} filtro={busca} />}
                  {can('tipos_servico') && <SidebarLink icon="🔧" label="Tipos de Serviço" active={currentView === 'tipos_servico'} onClick={() => onNavigate('tipos_servico')} filtro={busca} />}
                  {can('tipos_padrao') && <SidebarLink icon="⚡" label="Tipos de Padrão" active={currentView === 'tipos_padrao'} onClick={() => onNavigate('tipos_padrao')} filtro={busca} />}
                  {can('cadastro_inversores_modulos') && <SidebarLink icon="⚡" label="Inversores e Módulos" active={currentView === 'cadastro_inversores_modulos'} onClick={() => onNavigate('cadastro_inversores_modulos')} filtro={busca} />}
                  {isAdmin && <SidebarLink icon="🏢" label="Empresas" active={false} onClick={onOpenEmpresas} filtro={busca} />}
                </SidebarGroup>

                <SidebarGroup title="Financeiro" icon="💰" filtro={busca} abertoPorAtivo={grupoFinanceiroAberto}>
                  {can('financeiro') && <SidebarLink icon="💰" label="Financeiro" active={currentView === 'financeiro'} onClick={() => onNavigate('financeiro')} filtro={busca} />}
                  {can('lancar_movimentacoes') && <SidebarLink icon="📥" label="Lançar Movimentações" active={currentView === 'lancar_movimentacoes'} onClick={() => onNavigate('lancar_movimentacoes')} filtro={busca} />}
                  {can('cadastro_tipos_movimentacao') && <SidebarLink icon="📋" label="Tipos de Movimentação" active={currentView === 'cadastro_tipos_movimentacao'} onClick={() => onNavigate('cadastro_tipos_movimentacao')} filtro={busca} />}
                  {can('pagamento_colaborador') && <SidebarLink icon="💰" label="Pagamento Colaborador" active={currentView === 'pagamento_colaborador'} onClick={() => onNavigate('pagamento_colaborador')} filtro={busca} />}
                  {can('adiantamento') && <SidebarLink icon="💸" label="Adiantamento" active={currentView === 'adiantamento'} onClick={() => onNavigate('adiantamento')} filtro={busca} />}
                  {can('medicao') && <SidebarLink icon="📏" label="Medição" active={currentView === 'medicao'} onClick={() => onNavigate('medicao')} filtro={busca} />}
                </SidebarGroup>

                <SidebarGroup title="Ferramentas" icon="🧰" filtro={busca} abertoPorAtivo={grupoFerramentasAberto}>
                  {can('calculadora_solar') && <SidebarLink icon="☀️" label="Calculadora Solar" active={currentView === 'calculadora_solar'} onClick={() => onNavigate('calculadora_solar')} filtro={busca} />}
                  {can('calendario') && <SidebarLink icon="📅" label="Calendário" active={currentView === 'calendario'} onClick={() => onNavigate('calendario')} filtro={busca} />}
                  {can('valor_kit') && <SidebarLink icon="💵" label="Valores de Kit" active={false} onClick={onOpenValorKit} filtro={busca} />}
                  {can('kits_personalizados') && <SidebarLink icon="🔧" label="Kits Personalizados" active={false} onClick={onOpenKitsPersonalizados} filtro={busca} />}
                  {can('kit_fornecedores') && <SidebarLink icon="🚚" label="Kit Fornecedores" active={currentView === 'kit_fornecedores'} onClick={() => onNavigate('kit_fornecedores')} filtro={busca} />}
                  {can('lista_material') && <SidebarLink icon="📦" label="Lista de Material" active={false} onClick={onOpenListaMaterial} filtro={busca} />}
                  {isAdmin && <SidebarLink icon="☀️" label="Simulação Fotus" active={currentView === 'simulacao_fotus'} onClick={() => onNavigate('simulacao_fotus')} filtro={busca} />}
                  {(isAdmin || userRole === USER_ROLES.GERENTE || userRole === USER_ROLES.ESPECIALISTA || chatZapLiberado || propostaLiberada || can('whatsapp')) && (
                    <SidebarLink
                      icon="💬"
                      label={userRole === USER_ROLES.ADMINISTRADOR ? 'API Whatsapp' : 'WhatsApp CRM'}
                      active={currentView === 'whatsapp'}
                      onClick={() => onNavigate('whatsapp')}
                      filtro={busca}
                    />
                  )}
                </SidebarGroup>
              </>
            )}
          </ul>
        </div>

        <div className="app-sidebar-footer">
          <button type="button" className="app-sidebar-footer-btn" onClick={onOpenChangePassword} data-bs-dismiss="offcanvas">
            Alterar senha
          </button>
          <button type="button" className="app-sidebar-footer-btn is-danger" onClick={onLogout}>
            Sair
          </button>
        </div>
      </div>
    </>
  )
}
